import { type CommandProperties, createCommand } from '@atlassian/rovo-content-bridge-api';

export type ContentContext = 'staging-area' | 'global';

/**
 * @description Content type surfaces (e.g. Confluence Page editor, Confluence Whiteboard) should emit this command
 * when they have mounted and are ready to communicate with the parent Rovo chat instance.
 */
export type ContentReadyCommandProperties = CommandProperties & {
	/**
	 * The type of content the surface represents.
	 */
	contentType: 'page' | 'whiteboard' | 'space' | 'database';

	/**
	 * The host product's content identifier for this surface (e.g., Confluence contentId).
	 */
	contentId: string;

	/**
	 * Whether the content target can provide context for create and edit mutations.
	 */
	canProvideContext: boolean;

	/**
	 * The context in which this content is being rendered - staging area, or global Rovo chat.
	 * @default 'global'
	 */
	contentContext: ContentContext;
};

/**
 * A command sent by a bridge API client to announce that a content type surface is ready.
 *
 * @summary {@link CommandProperties.senderId} is notifying {@link CommandProperties.receiverId} that the
 * current content type surface is mounted and can provide context/data. This can be used to determine what the
 * parent Rovo chat instance should target when making mutations.
 */
export const ContentReadyCommand = createCommand<ContentReadyCommandProperties, void>(
	'content-ready',
);

/**
 * @description Content type surfaces (e.g. Confluence Page editor, Confluence Whiteboard) should emit this command
 * when they have are about to unmount and want to communicate this state to the Rovo store.
 */
export type ContentDestroyCommandProperties = CommandProperties & {
	/**
	 * The type of content the surface represents.
	 */
	contentType: 'page' | 'whiteboard' | 'space' | 'database';

	/**
	 * The host product's content identifier for this surface (e.g., Confluence contentId).
	 */
	contentId: string;

	/**
	 * The context in which this content is being rendered - staging area, or global Rovo chat.
	 * @default 'global'
	 */
	contentContext: ContentContext;
};

/**
 * A command sent by a bridge API client to announce it is about to be destroyed.
 *
 * @summary {@link CommandProperties.senderId} is notifying {@link CommandProperties.receiverId} that the
 * current content type surface is about to be unmounted, and therefore inaccessible to the user.
 */
export const ContentDestroyCommand = createCommand<ContentDestroyCommandProperties, void>(
	'content-destroy',
);
